import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from '@angular/common';

export interface SeoConfig {
    title: string;
    description: string;
    /** Route path without locale prefix, e.g. '/premium' or '/learn/first-steps'. */
    path?: string;
    image?: string;
    noIndex?: boolean;
    /**
     * Locales this page actually exists in. Defaults to all of them, which is right for the
     * `$localize`-translated pages. Authored marketing pages (src/app/content) pass their own
     * shorter set, so hreflang never advertises a URL that was never built.
     */
    locales?: readonly string[];
    /**
     * Path of this page's markdown twin, e.g. '/games/everdell.md' (LUD-352). Absolute on the site,
     * never locale-prefixed. Only the public game and community pages have one.
     */
    markdown?: string;
}

/** The 1200x630 logo-and-name card; the API's site-meta pages point at this same file. */
export const DEFAULT_SHARE_IMAGE = 'https://www.ludoya.com/assets/og/default.png';

@Injectable({providedIn: 'root'})
export class SeoService {

    private readonly baseUrl = 'https://www.ludoya.com';
    private readonly defaultImage = DEFAULT_SHARE_IMAGE;
    private readonly logo = 'https://www.ludoya.com/assets/logo.png';
    private readonly supportedLocales = [
        'en', 'es', 'ca', 'de', 'fr', 'hu', 'it', 'nl', 'no', 'pl', 'pt', 'ro',
        'ja', 'zh', 'ru', 'ko', 'tr', 'sv', 'da', 'fi', 'cs', 'ar', 'bg',
    ];

    constructor(
        private titleService: Title,
        private meta: Meta,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE_ID) private locale: string,
    ) {
    }

    update(config: SeoConfig): void {
        // Title
        const fullTitle = config.title ? `${config.title} | Ludoya` : 'Ludoya';
        this.titleService.setTitle(fullTitle);

        // Description
        this.meta.updateTag({name: 'description', content: config.description});

        // Robots
        this.meta.updateTag({
            name: 'robots',
            content: config.noIndex ? 'noindex, nofollow' : 'index, follow',
        });

        const canonical = this.buildCanonical(config.path);
        const image = config.image || this.defaultImage;

        // Open Graph
        this.meta.updateTag({property: 'og:type', content: 'website'});
        this.meta.updateTag({property: 'og:site_name', content: 'Ludoya'});
        this.meta.updateTag({property: 'og:locale', content: this.locale});
        this.meta.updateTag({property: 'og:title', content: fullTitle});
        this.meta.updateTag({property: 'og:description', content: config.description});
        this.meta.updateTag({property: 'og:url', content: canonical});
        this.meta.updateTag({property: 'og:image', content: image});

        // Twitter
        this.meta.updateTag({name: 'twitter:card', content: 'summary_large_image'});
        this.meta.updateTag({name: 'twitter:title', content: fullTitle});
        this.meta.updateTag({name: 'twitter:description', content: config.description});
        this.meta.updateTag({name: 'twitter:url', content: canonical});
        this.meta.updateTag({name: 'twitter:image', content: image});

        // Canonical + hreflang
        this.setCanonical(canonical);
        this.setHreflang(config.path ?? '/', config.locales ?? this.supportedLocales);
        this.setMarkdownAlternate(config.markdown);
    }

    /**
     * Points at the page's `.md` twin (LUD-352), so an assistant fetching this HTML can discover the
     * layout-free version instead of guessing the URL. Removed again on pages that have none, since
     * this runs on every navigation of a single-page app.
     */
    private setMarkdownAlternate(path?: string): void {
        const existing = this.document.head.querySelector('link[rel="alternate"][type="text/markdown"]');
        if (!path) {
            existing?.remove();
            return;
        }
        const link = existing as HTMLLinkElement ?? this.document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('type', 'text/markdown');
        // Not locale-prefixed: the .md files are English only, and one of them serves every locale.
        link.setAttribute('href', `${this.baseUrl}${path}`);
        if (!existing) {
            this.document.head.appendChild(link);
        }
    }

    /** Builds the absolute, locale-prefixed canonical URL for a route path. */
    private buildCanonical(path?: string): string {
        const clean = !path ? '/' : (path.startsWith('/') ? path : `/${path}`);
        const localePrefix = this.locale === 'en' ? '' : `/${this.locale}`;
        return `${this.baseUrl}${localePrefix}${clean}`;
    }

    /** Public locale-aware canonical builder, for use in structured data. */
    canonical(path?: string): string {
        return this.buildCanonical(path);
    }

    /**
     * Reusable Organization node for JSON-LD structured data.
     *
     * `description` is the canonical sentence (docs/canonical-description.md) — the same one used on
     * the off-site profiles, so every description of Ludoya a knowledge graph finds agrees with the
     * others. `sameAs` is what ties those profiles to this domain: without it each listing is an
     * unrelated page about something called Ludoya, and an answer engine is free to invent what the
     * product does from whichever one it read. Deliberately English and not `$localize`d: this is an
     * entity description for machines, and it must read identically from every locale of the site.
     *
     * Wikidata joins `sameAs` once the entry is published (LUD-366).
     */
    organizationSchema(): object {
        return {
            '@type': 'Organization',
            '@id': `${this.baseUrl}/#organization`,
            name: 'Ludoya',
            url: this.baseUrl,
            logo: this.logo,
            description:
                'Ludoya is a free board game platform for players, clubs and shops: log your plays ' +
                'and keep your collection, find and run game nights, tournaments and conventions ' +
                'near you, run a club with its members, library and events, and buy, sell or borrow ' +
                'games through local shops and other players. On web, Android and iOS.',
            sameAs: [
                'https://play.google.com/store/apps/details?id=com.ludoya.app',
                'https://apps.apple.com/app/ludoya/id6677054406',
                'https://twitter.com/ludoyapp',
                'https://t.me/ludoyapp',
                'https://discord.gg/QG2yNYypVn',
            ],
        };
    }

    setJsonLd(schema: object | object[]): void {
        // Remove existing JSON-LD
        const existing = this.document.head.querySelector('script[type="application/ld+json"]');
        if (existing) {
            existing.remove();
        }

        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        this.document.head.appendChild(script);
    }

    private setCanonical(url: string): void {
        let link = this.document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement;
        if (!link) {
            link = this.document.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.document.head.appendChild(link);
        }
        link.setAttribute('href', url);
    }

    private setHreflang(path: string, locales: readonly string[]): void {
        // Remove existing hreflang links
        this.document.head.querySelectorAll('link[hreflang]').forEach(el => el.remove());

        const routePath = path.startsWith('/') ? path : `/${path}`;

        const add = (hreflang: string, href: string) => {
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', hreflang);
            link.setAttribute('href', href);
            this.document.head.appendChild(link);
        };

        const href = (loc: string) => loc === 'en'
            ? `${this.baseUrl}${routePath}`
            : `${this.baseUrl}/${loc}${routePath}`;

        for (const loc of locales) {
            add(loc, href(loc));
        }
        // x-default points at English when the page has it, and otherwise at the first locale it
        // does have — a page authored only in Spanish still needs a default to send the rest of
        // the world to.
        const fallback = locales.includes('en') ? 'en' : locales[0];
        if (fallback) {
            add('x-default', href(fallback));
        }
    }
}
